import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Activity, MessageSquare, DollarSign, Cpu, Wallet, Save, Play } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { AdminShell } from '@/components/admin-shell';
import { api, ApiCallError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const PANEL = 'rounded-lg border border-border bg-background shadow-sm';

/**
 * Modelos curados que ofrecemos en los dropdowns. Editable acá. Si la config
 * guardada usa un modelo fuera de esta lista, igual se muestra (se agrega al vuelo).
 */
const MODEL_OPTIONS = [
  'deepseek/deepseek-v4-flash',
  'openai/gpt-4o-mini',
  'openai/gpt-4.1-nano',
  'anthropic/claude-3.5-haiku',
  'google/gemini-2.5-flash',
];

function modelOptionsWith(current: string): string[] {
  return MODEL_OPTIONS.includes(current) ? MODEL_OPTIONS : [current, ...MODEL_OPTIONS];
}

function ModelSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {modelOptionsWith(value).map((m) => (
          <SelectItem key={m} value={m}>
            {m}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Tipos (espejan admin-internal.ts del backend) ──────────────────────────
interface UsageBucket {
  label: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  requests: number;
  estimatedCostUsd: number | null;
}
interface TokenUsageStats {
  byDate: UsageBucket[];
  byModel: UsageBucket[];
  totals: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    requests: number;
    estimatedCostUsd: number;
  };
  topModel: string | null;
  days: number;
}
interface ModelConfig {
  tools: string;
  chat: string;
  fallback: { enabled: boolean; model: string };
}
interface KeyInfo {
  masked: string | null;
  source: 'override' | 'none';
}
interface PlatformModels {
  vision: string;
  visionFallback: string;
  text: string;
  embedding: string;
  geminiBatch: string;
}
interface ModelsResponse {
  config: ModelConfig;
  platform: PlatformModels;
  balance: { totalCredits: number; totalUsage: number; remaining: number } | null;
  keys: { openrouter: KeyInfo; openai: KeyInfo; gemini: KeyInfo };
  keyEditingAvailable: boolean;
}

// ── Helpers de formato ──────────────────────────────────────────────────────
const nf = new Intl.NumberFormat('es-PY');
const fmtTokens = (n: number) => nf.format(Math.round(n));
const fmtK = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(Math.round(n)));
const fmtUsd = (n: number | null) => (n === null ? '—' : `US$ ${n.toFixed(4)}`);

export function AdminInternalPage() {
  return (
    <AdminShell>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-foreground">IA</h1>
        <p className="text-sm text-muted-foreground">
          Consumo de tokens y configuración de modelos del bot.
        </p>
      </div>

      <Tabs defaultValue="consumo">
        <TabsList className="inline-flex w-auto">
          <TabsTrigger value="consumo">Consumo</TabsTrigger>
          <TabsTrigger value="modelos">Modelos</TabsTrigger>
        </TabsList>
        <TabsContent value="consumo" className="mt-4">
          <ConsumoTab />
        </TabsContent>
        <TabsContent value="modelos" className="mt-4">
          <ModelosTab />
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
}

// ── Tab: Consumo ────────────────────────────────────────────────────────────
function ConsumoTab() {
  const [preset, setPreset] = useState<'7' | '30' | '90' | 'custom'>('30');
  const [range, setRange] = useState<DateRange | undefined>();

  const { data, isLoading } = useQuery({
    queryKey: ['llm-usage', preset, range?.from?.toISOString(), range?.to?.toISOString()],
    queryFn: () => {
      if (preset === 'custom' && range?.from) {
        const from = format(range.from, 'yyyy-MM-dd');
        const to = format(range.to ?? range.from, 'yyyy-MM-dd');
        return api<TokenUsageStats>(`/api/admin/internal/usage?from=${from}&to=${to}`);
      }
      const days = preset === 'custom' ? 30 : preset;
      return api<TokenUsageStats>(`/api/admin/internal/usage?days=${days}`);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Select value={preset} onValueChange={(v) => setPreset(v as typeof preset)}>
          <SelectTrigger className="w-auto min-w-[170px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 días</SelectItem>
            <SelectItem value="30">Últimos 30 días</SelectItem>
            <SelectItem value="90">Últimos 90 días</SelectItem>
            <SelectItem value="custom">Rango personalizado</SelectItem>
          </SelectContent>
        </Select>
        {preset === 'custom' && <DateRangePicker value={range} onChange={setRange} />}
      </div>

      {isLoading || !data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard icon={<Activity className="size-4" />} label="Tokens totales">
              <div className="text-2xl font-bold tabular-nums text-foreground">
                {fmtTokens(data.totals.totalTokens)}
              </div>
              <div className="text-xs text-muted-foreground">
                {fmtK(data.totals.promptTokens)} in · {fmtK(data.totals.completionTokens)} out
              </div>
            </StatCard>
            <StatCard icon={<MessageSquare className="size-4" />} label="Solicitudes">
              <div className="text-2xl font-bold tabular-nums text-foreground">
                {fmtTokens(data.totals.requests)}
              </div>
            </StatCard>
            <StatCard icon={<DollarSign className="size-4" />} label="Costo estimado">
              <div className="text-2xl font-bold tabular-nums text-foreground">
                {fmtUsd(data.totals.estimatedCostUsd)}
              </div>
            </StatCard>
            <StatCard icon={<Cpu className="size-4" />} label="Modelo principal">
              <div className="break-all text-sm font-semibold text-foreground">
                {data.topModel ?? '—'}
              </div>
            </StatCard>
          </div>

          <UsageChart byDate={data.byDate} />

          <div className={`${PANEL} p-4`}>
            <h3 className="mb-3 text-sm font-semibold text-foreground">Por modelo</h3>
            {data.byModel.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin consumo en el período.</p>
            ) : (
              <div className="space-y-3">
                {data.byModel.map((m) => (
                  <div key={m.label} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">{m.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {m.requests} solicitudes · {fmtK(m.totalTokens)} tokens
                      </div>
                    </div>
                    <div className="shrink-0 text-sm font-semibold tabular-nums">
                      {fmtUsd(m.estimatedCostUsd)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Consumo system-wide. Costo estimado con los precios reales de OpenRouter (tokens × precio por
            modelo). El gasto exacto es el saldo de la cuenta (pestaña Modelos).
          </p>
        </>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`${PANEL} p-4`}>
      <div className="mb-1 flex items-center gap-1.5 text-sm text-muted-foreground">
        {icon}
        {label}
      </div>
      {children}
    </div>
  );
}

function UsageChart({ byDate }: { byDate: UsageBucket[] }) {
  const points = byDate.slice(-14);
  const max = Math.max(1, ...points.map((d) => d.totalTokens));
  return (
    <div className={`${PANEL} p-4`}>
      <h3 className="mb-4 text-sm font-semibold text-foreground">Tokens por día</h3>
      {points.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sin datos.</p>
      ) : (
        <>
          <div className="flex h-40 items-stretch gap-1">
            {points.map((d) => {
              const inH = (d.promptTokens / max) * 100;
              const outH = (d.completionTokens / max) * 100;
              return (
                <div
                  key={d.label}
                  className="flex h-full flex-1 flex-col items-center gap-1"
                  title={`${d.label}: ${fmtTokens(d.totalTokens)} tokens`}
                >
                  <div className="flex w-full flex-1 flex-col justify-end">
                    <div className="w-full rounded-t-sm bg-primary/40" style={{ height: `${outH}%` }} />
                    <div className="w-full bg-primary" style={{ height: `${inH}%` }} />
                  </div>
                  <span className="text-[10px] text-muted-foreground">{d.label.slice(5)}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex justify-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <span className="inline-block size-2 rounded-sm bg-primary" /> entrada
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block size-2 rounded-sm bg-primary/40" /> salida
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab: Modelos ────────────────────────────────────────────────────────────
function ModelosTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['llm-models'],
    queryFn: () => api<ModelsResponse>('/api/admin/internal/models'),
  });

  if (isLoading || !data) return <Skeleton className="h-96 w-full" />;
  return <ModelosForm data={data} onChanged={() => qc.invalidateQueries({ queryKey: ['llm-models'] })} />;
}

function ModelosForm({ data, onChanged }: { data: ModelsResponse; onChanged: () => void }) {
  const [chat, setChat] = useState(data.config.chat);
  const [tools, setTools] = useState(data.config.tools);
  const [fallbackEnabled, setFallbackEnabled] = useState(data.config.fallback.enabled);
  const [fallbackModel, setFallbackModel] = useState(data.config.fallback.model);
  const [orKeyInput, setOrKeyInput] = useState('');
  const [oaKeyInput, setOaKeyInput] = useState('');
  const [geKeyInput, setGeKeyInput] = useState('');
  const [platform, setPlatform] = useState<PlatformModels>(data.platform);
  const [testMsg, setTestMsg] = useState('');
  const [testResult, setTestResult] = useState<{
    intent: string;
    model: string;
    text: string;
    promptTokens: number;
    completionTokens: number;
  } | null>(null);

  const errMsg = (e: unknown, fallback: string) =>
    e instanceof ApiCallError ? e.body.message ?? e.body.error : fallback;

  const saveConfig = useMutation({
    mutationFn: () =>
      api<{ config: ModelConfig }>('/api/admin/internal/models', {
        method: 'PUT',
        body: JSON.stringify({ chat, tools, fallback: { enabled: fallbackEnabled, model: fallbackModel } }),
      }),
    onSuccess: () => {
      toast.success('Configuración guardada. Se aplica en hasta 30 segundos.');
      onChanged();
    },
    onError: (e) => toast.error(errMsg(e, 'Error al guardar')),
  });

  const savePlatform = useMutation({
    mutationFn: () =>
      api<{ platform: PlatformModels }>('/api/admin/internal/models/platform', {
        method: 'PUT',
        body: JSON.stringify(platform),
      }),
    onSuccess: () => {
      toast.success('Modelos de plataforma guardados. Se aplican en hasta 30 segundos.');
      onChanged();
    },
    onError: (e) => toast.error(errMsg(e, 'Error al guardar')),
  });

  const setPlat = (k: keyof PlatformModels, v: string) =>
    setPlatform((p) => ({ ...p, [k]: v }));

  const saveKey = useMutation({
    mutationFn: (p: { provider: 'openrouter' | 'openai' | 'gemini'; key: string }) =>
      api<KeyInfo>('/api/admin/internal/keys', { method: 'PUT', body: JSON.stringify(p) }),
    onSuccess: () => {
      toast.success('API key guardada.');
      setOrKeyInput('');
      setOaKeyInput('');
      setGeKeyInput('');
      onChanged();
    },
    onError: (e) => toast.error(errMsg(e, 'Error al guardar la key')),
  });

  const runTest = useMutation({
    mutationFn: () =>
      api<{ intent: string; model: string; text: string; promptTokens: number; completionTokens: number }>(
        '/api/admin/internal/models/test',
        { method: 'POST', body: JSON.stringify({ message: testMsg }) }
      ),
    onSuccess: (r) => setTestResult(r),
    onError: (e) => toast.error(errMsg(e, 'Error en la prueba')),
  });

  return (
    <div className="space-y-4">
      {/* Cuenta OpenRouter */}
      <div className={`${PANEL} space-y-3 p-4`}>
        <div className="flex items-center gap-2 font-semibold text-foreground">
          <Wallet className="size-4" /> Cuenta OpenRouter
        </div>
        {data.balance ? (
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-xs text-muted-foreground">Saldo</div>
              <div className="font-semibold tabular-nums">US$ {data.balance.remaining.toFixed(4)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Consumido</div>
              <div className="font-semibold tabular-nums">US$ {data.balance.totalUsage.toFixed(4)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Total</div>
              <div className="font-semibold tabular-nums">US$ {data.balance.totalCredits.toFixed(2)}</div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Saldo no disponible.</p>
        )}
        <div className="text-xs text-muted-foreground">
          API key:{' '}
          <code className="font-mono">{data.keys.openrouter.masked ?? 'sin configurar'}</code>
        </div>
        {data.keyEditingAvailable ? (
          <div className="space-y-2">
            <Input
              placeholder="Nueva API key (sk-or-...)"
              value={orKeyInput}
              onChange={(e) => setOrKeyInput(e.target.value)}
            />
            <Button
              size="sm"
              disabled={!orKeyInput || saveKey.isPending}
              onClick={() => saveKey.mutate({ provider: 'openrouter', key: orKeyInput })}
            >
              Guardar clave
            </Button>
          </div>
        ) : (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
            Para guardar API keys desde acá (cifradas en la base, no en el .env) falta la clave maestra de
            cifrado. Generala una sola vez con <code className="font-mono">openssl rand -base64 32</code> y
            ponela como <code className="font-mono">SET_CREDENTIALS_KEY</code> en las variables de entorno
            del deploy. Después podés cargar/cambiar las keys desde este panel.
          </div>
        )}
      </div>

      {/* OpenAI */}
      <div className={`${PANEL} space-y-3 p-4`}>
        <div className="font-semibold text-foreground">OpenAI (Whisper / audio)</div>
        <div className="text-xs text-muted-foreground">
          API key: <code className="font-mono">{data.keys.openai.masked ?? 'sin configurar'}</code>
        </div>
        {data.keyEditingAvailable && (
          <div className="space-y-2">
            <Input
              placeholder="Nueva API key (sk-...)"
              value={oaKeyInput}
              onChange={(e) => setOaKeyInput(e.target.value)}
            />
            <Button
              size="sm"
              disabled={!oaKeyInput || saveKey.isPending}
              onClick={() => saveKey.mutate({ provider: 'openai', key: oaKeyInput })}
            >
              Guardar clave
            </Button>
          </div>
        )}
      </div>

      {/* Gemini (OCR batch) */}
      <div className={`${PANEL} space-y-3 p-4`}>
        <div className="font-semibold text-foreground">Gemini (OCR batch de facturas)</div>
        <div className="text-xs text-muted-foreground">
          Key directa de Google AI Studio (no la de OpenRouter). Activa el path batch de extracción
          (~50% más barato). Sin ella, la extracción del bot cae a sync por OpenRouter.
        </div>
        <div className="text-xs text-muted-foreground">
          API key: <code className="font-mono">{data.keys.gemini.masked ?? 'sin configurar'}</code>
        </div>
        {data.keyEditingAvailable && (
          <div className="space-y-2">
            <Input
              placeholder="Nueva API key de Gemini"
              value={geKeyInput}
              onChange={(e) => setGeKeyInput(e.target.value)}
            />
            <Button
              size="sm"
              disabled={!geKeyInput || saveKey.isPending}
              onClick={() => saveKey.mutate({ provider: 'gemini', key: geKeyInput })}
            >
              Guardar clave
            </Button>
          </div>
        )}
      </div>

      {/* Modelos por tipo de tarea */}
      <div className={`${PANEL} space-y-4 p-4`}>
        <div className="font-semibold text-foreground">Modelo por tipo de tarea</div>
        <div className="space-y-1">
          <Label>Conversacional</Label>
          <p className="text-xs text-muted-foreground">Saludos, menú, preguntas de ayuda</p>
          <ModelSelect value={chat} onChange={setChat} />
        </div>
        <div className="space-y-1">
          <Label>Acciones (tools)</Label>
          <p className="text-xs text-muted-foreground">Onboarding, facturación, cobros, consultas con datos</p>
          <ModelSelect value={tools} onChange={setTools} />
        </div>
      </div>

      {/* Fallback */}
      <div className={`${PANEL} space-y-3 p-4`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-foreground">Fallback</div>
            <p className="text-xs text-muted-foreground">Si el modelo primario falla, reintenta con este.</p>
          </div>
          <Badge
            className="cursor-pointer"
            variant={fallbackEnabled ? 'default' : 'outline'}
            onClick={() => setFallbackEnabled((v) => !v)}
          >
            {fallbackEnabled ? 'Activado' : 'Desactivado'}
          </Badge>
        </div>
        {fallbackEnabled && <ModelSelect value={fallbackModel} onChange={setFallbackModel} />}
      </div>

      <div className="space-y-1">
        <Button disabled={saveConfig.isPending} onClick={() => saveConfig.mutate()}>
          <Save className="mr-2 size-4" /> Guardar configuración
        </Button>
        <p className="text-xs text-muted-foreground">Los cambios se aplican en hasta 30 segundos.</p>
      </div>

      {/* Modelos de plataforma (OCR / embeddings / batch) */}
      <div className={`${PANEL} space-y-4 p-4`}>
        <div>
          <div className="font-semibold text-foreground">Modelos de plataforma</div>
          <p className="text-xs text-muted-foreground">
            OCR de facturas, embeddings del RAG y batch de Gemini. No son los modelos del bot
            (arriba); son los de infraestructura. String del modelo (ej. <code>google/gemini-2.5-flash</code>).
          </p>
        </div>
        <div className="space-y-1">
          <Label>OCR — visión (primario)</Label>
          <Input value={platform.vision} onChange={(e) => setPlat('vision', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>OCR — fallback (confianza baja)</Label>
          <Input
            value={platform.visionFallback}
            onChange={(e) => setPlat('visionFallback', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label>Texto (fallback del command-router)</Label>
          <Input value={platform.text} onChange={(e) => setPlat('text', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Embeddings (RAG)</Label>
          <Input value={platform.embedding} onChange={(e) => setPlat('embedding', e.target.value)} />
          <p className="text-xs text-amber-600 dark:text-amber-400">
            ⚠ Cambiar el modelo de embeddings invalida los vectores ya indexados — hay que
            re-embeber el corpus del RAG.
          </p>
        </div>
        <div className="space-y-1">
          <Label>OCR batch (Gemini Batch API)</Label>
          <Input
            value={platform.geminiBatch}
            onChange={(e) => setPlat('geminiBatch', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Button disabled={savePlatform.isPending} onClick={() => savePlatform.mutate()}>
            <Save className="mr-2 size-4" /> Guardar modelos de plataforma
          </Button>
          <p className="text-xs text-muted-foreground">Los cambios se aplican en hasta 30 segundos.</p>
        </div>
      </div>

      {/* Probar en vivo */}
      <div className={`${PANEL} space-y-3 p-4`}>
        <div className="font-semibold text-foreground">Probar en vivo</div>
        <p className="text-xs text-muted-foreground">
          Mandá un mensaje de ejemplo y mirá qué tier se clasifica y qué modelo responde.
        </p>
        <Input
          placeholder="Ej: facturá 50.000 a Juan"
          value={testMsg}
          onChange={(e) => setTestMsg(e.target.value)}
        />
        <Button
          variant="outline"
          disabled={!testMsg || runTest.isPending}
          onClick={() => runTest.mutate()}
        >
          <Play className="mr-2 size-4" /> {runTest.isPending ? 'Probando…' : 'Probar'}
        </Button>
        {testResult && (
          <div className="space-y-1 rounded-md bg-muted p-3 text-sm">
            <div className="text-xs text-muted-foreground">
              intent: <span className="font-medium">{testResult.intent}</span> · modelo:{' '}
              <span className="font-medium">{testResult.model}</span> · {testResult.promptTokens}+
              {testResult.completionTokens} tokens
            </div>
            <div className="whitespace-pre-wrap">{testResult.text}</div>
          </div>
        )}
      </div>
    </div>
  );
}
